import { Org_Module_Enum, Org_Plan_Type_Enum } from '../graphql/graphql'

/**
 * Ce qu'un forfait ouvre, et ce qu'il ferme.
 *
 * 🔑 **La règle ne vit plus ici : elle vit en base**, dans `org_plan_module`
 * (ADM06-D5). Ce fichier n'en garde que les fonctions, qui la reçoivent en
 * argument.
 *
 * Pourquoi ce déménagement : le verrouillage côté serveur passe par les
 * permissions Hasura, et Hasura ne sait pas lire du TypeScript. Laisser la
 * règle ici en aurait imposé une seconde copie dans dix fichiers YAML — le
 * motif exact qui a fait diverger `readGovernanceChanges`. Une seule source,
 * en base, que les deux consultent.
 */

/**
 * Une ligne de la règle, telle qu'elle arrive de la base.
 *
 * ⚠️ Volontairement structurel plutôt que nominal : `shared` est lu par le
 * backend comme par la webapp, dont les types générés vivent dans deux
 * fichiers distincts. Une forme minimale accepte les deux sans les coupler.
 */
export interface PlanModuleRule {
  plan: Org_Plan_Type_Enum
  module: Org_Module_Enum
}

/**
 * L'ordre de la grille tarifaire, parmi les formules qu'on achète.
 *
 * 🔑 Ceci n'est pas la règle, c'est un ordre de présentation — il répond à
 * « laquelle proposer en premier », jamais à « qu'ouvre-t-elle ». Il reste donc
 * dans le code sans rien dupliquer de la base.
 *
 * ⚠️ `Free` en est absente à dessein : l'essai ouvre tout, il ne renseigne pas
 * sur ce qu'il faut acheter pour garder un module.
 */
export const PURCHASABLE_PLANS: Org_Plan_Type_Enum[] = [
  Org_Plan_Type_Enum.CartoSolo,
  Org_Plan_Type_Enum.CartoGroov,
]

/**
 * Le forfait ouvre-t-il ce module ?
 *
 * 🔑 **`planEndsAt` n'est PAS lu ici, et c'est voulu.** Une date passée ne ferme
 * rien par elle-même : c'est le cron de cycle de vie qui, à l'expiration, pose
 * `disabledAt` — et c'est `disabledAt` qui ferme tout, en amont de cette
 * fonction. Deux avantages : l'état de l'organisation reste lisible dans une
 * seule colonne, et Yohan peut rouvrir un accès d'un clic depuis sa console
 * sans avoir à repousser une date.
 */
export function isModuleEnabled(
  rules: PlanModuleRule[],
  plan: Org_Plan_Type_Enum,
  module: Org_Module_Enum
): boolean {
  return rules.some((rule) => rule.plan === plan && rule.module === module)
}

/**
 * La première formule achetable qui ouvre ce module, dans l'ordre tarifaire.
 *
 * 🔑 Le message « Disponible avec la formule X » se déduit de la règle au lieu
 * d'être écrit à côté d'elle : le jour où un module change de formule, la phrase
 * suit toute seule au lieu de devenir fausse en silence.
 */
export function firstPlanWithModule(
  rules: PlanModuleRule[],
  module: Org_Module_Enum
): Org_Plan_Type_Enum | undefined {
  return PURCHASABLE_PLANS.find((plan) => isModuleEnabled(rules, plan, module))
}

/**
 * Jours restants avant une date. Négatif = déjà passée.
 *
 * 🔑 Ici plutôt qu'en trois exemplaires. Ce calcul vivait dans la console
 * d'administration, dans la vitrine d'abonnement et dans le cron de cycle de
 * vie — trois copies de la même division, donc trois occasions de n'en corriger
 * qu'une. C'est le motif qui avait fait diverger `readGovernanceChanges`.
 */
export function daysUntil(date: string, from: Date = new Date()): number {
  return Math.ceil((new Date(date).getTime() - from.getTime()) / 86400000)
}
